import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { HERO, IMAGES } from "../data/content";
import { PillButton } from "../components/PillButton";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { EditorialMarquee } from "../components/EditorialMarquee";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} className="relative pt-32 md:pt-40 pb-20 overflow-hidden" data-testid="hero">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center min-h-[86vh]">
        {/* Left — text */}
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-xs uppercase tracking-[0.28em] text-sage-dark mb-6"
          >
            Studio di Psicologia e Psicoterapia · Macerata, Marche
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-ink text-3xl sm:text-4xl leading-none"
          >
            Dott.ssa Susanna Paoletti
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-3 font-serif text-sage-dark text-xl sm:text-2xl tracking-tight"
          >
            Psicologa e Psicoterapeuta a Macerata
          </motion.h1>

          <p className="mt-7 font-serif text-ink tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-[4.25rem]">
            {HERO.titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-7 text-lg md:text-xl text-stone leading-relaxed max-w-xl"
          >
            {HERO.heroSubtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <PillButton to="/contatti" testid="hero-cta-primary">
              Richiedi un primo colloquio <ArrowRight size={18} />
            </PillButton>
            <PillButton to="/approccio" variant="outline" testid="hero-cta-secondary">
              Scopri il mio approccio
            </PillButton>
          </motion.div>
        </div>

        {/* Right — portrait, arched clipped frame */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div
              className="relative overflow-hidden bg-sand"
              style={{ borderRadius: "50% 50% 1.5rem 1.5rem / 32% 32% 1.5rem 1.5rem" }}
            >
              <motion.img
                src={IMAGES.portrait}
                alt="Dott.ssa Susanna Paoletti, psicologa e psicoterapeuta a Macerata"
                width={900}
                height={1241}
                fetchPriority="high"
                decoding="async"
                style={{ y: yImg, scale: scaleImg }}
                className="w-full h-[440px] md:h-[560px] object-cover object-top will-change-transform"
                loading="eager"
              />
            </div>
            <div className="mt-6 text-center">
              <p className="font-serif text-2xl md:text-3xl text-ink leading-tight">Dott.ssa Susanna Paoletti</p>
              <p className="mt-2 text-sm text-stone">Psicologa e Psicoterapeuta Cognitivo-Comportamentale · EMDR II Livello</p>
              <p className="mt-1 text-sm text-sage-dark tracking-wide">Oltre 20 anni di esperienza clinica e professionale</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* soft ambient shape */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-sage/10 blur-3xl" />
    </section>
  );
};

const Intro = () => (
  <section className="py-24 md:py-32 bg-sand" data-testid="home-intro">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-end">
      <div className="md:col-span-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] text-sage-dark mb-6">Chi sono</p>
        </Reveal>
        <Reveal delay={0.04}>
          <h2 className="font-serif text-3xl md:text-5xl text-ink tracking-tight leading-tight mb-8">
            Chi è la Dott.ssa Susanna Paoletti
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="font-serif text-2xl md:text-3xl text-ink leading-snug tracking-tight text-balance">
            {HERO.presentation}
          </p>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-10 font-serif italic text-3xl md:text-4xl text-stone">
            {HERO.presentationItalic}
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8">
            <span className="inline font-serif text-2xl md:text-3xl text-sage-dark leading-snug tracking-tight bg-sage/15 box-decoration-clone px-2 py-1 rounded">
              {HERO.presentationHighlight}
            </span>
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <p className="mt-10 text-base md:text-lg text-stone leading-relaxed max-w-2xl">
            Ricevo nel mio studio a Macerata, in Via Emanuele Filiberto 1, dove propongo percorsi di psicoterapia
            individuale e di coppia. Puoi conoscere il mio{" "}
            <Link to="/approccio" className="text-sage-dark underline underline-offset-2 hover:text-ink transition-colors">
              approccio cognitivo-comportamentale ed EMDR
            </Link>
            , consultare le{" "}
            <Link to="/aree-di-intervento#aree-intervento" className="text-sage-dark underline underline-offset-2 hover:text-ink transition-colors">
              aree di intervento
            </Link>{" "}
            oppure scrivermi per un{" "}
            <Link to="/contatti" className="text-sage-dark underline underline-offset-2 hover:text-ink transition-colors">
              primo colloquio
            </Link>
            .
          </p>
        </Reveal>
      </div>
      <div className="md:col-span-4 md:text-right">
        <Reveal delay={0.16}>
          <Link
            to="/chi-sono"
            className="group inline-flex items-center gap-2 text-ink hover:text-sage-dark transition-colors"
            data-testid="home-intro-link"
          >
            <span className="font-serif text-2xl">Conoscimi</span>
            <ArrowUpRight size={22} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const AREE_HOME = [
  { t: "Ansia, paure e attacchi di panico", slug: "ansia-panico", to: "/aree-di-intervento#aree-intervento" },
  { t: "Stress e difficoltà emotive", slug: "stress", to: "/aree-di-intervento#aree-intervento" },
  { t: "Depressione e disturbi dell'umore", slug: "depressione", to: "/aree-di-intervento#aree-intervento" },
  { t: "Difficoltà di coppia e relazionali", slug: "coppia", to: "/aree-di-intervento#aree-intervento" },
  { t: "Disturbo ossessivo-compulsivo", slug: "doc", to: "/aree-di-intervento#aree-intervento" },
  { t: "Trauma psicologico ed EMDR", slug: "trauma-emdr", to: "/approccio#emdr" },
];

const AreeTeaser = () => (
  <section className="py-24 md:py-32" data-testid="home-aree-teaser">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
        <Reveal>
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-sage-dark mb-5">Di cosa mi occupo</p>
            <h2 className="font-serif text-4xl md:text-6xl text-ink tracking-tight max-w-xl leading-tight">
              Percorsi di psicologia e psicoterapia
            </h2>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <PillButton to="/aree-di-intervento#aree-intervento" variant="outline" testid="home-aree-all">
            Vedi tutte le aree di intervento
          </PillButton>
        </Reveal>
      </div>

      <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {AREE_HOME.map((a) => (
          <StaggerItem key={a.slug}>
            <Link
              to={a.to}
              className="group h-full flex items-center justify-between gap-4 bg-sand border border-border rounded-2xl p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-sage"
              data-testid={`home-area-${a.slug}`}
            >
              <h3 className="font-serif text-2xl text-ink leading-snug">{a.t}</h3>
              <ArrowUpRight size={22} className="text-sage-dark shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </div>
  </section>
);

const Approcci = () => (
  <section className="py-24 md:py-32 bg-ink text-cream relative overflow-hidden" data-testid="home-approcci">
    <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.28em] text-sage-light mb-4">Approccio terapeutico</p>
      </Reveal>
      <Reveal delay={0.04}>
        <h2 className="font-serif text-4xl md:text-5xl mb-10 tracking-tight">Il mio approccio terapeutico</h2>
      </Reveal>
      <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
        <Reveal delay={0.06}>
          <div>
            <span className="font-serif text-5xl text-sage-light/70">01</span>
            <h3 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">Cognitivo-comportamentale</h3>
            <p className="text-cream/70 leading-relaxed mt-5 max-w-md">
              Un percorso orientato alla comprensione del rapporto tra pensieri, emozioni e comportamenti,
              per riconoscere ciò che ci limita e sperimentare modalità nuove.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.16}>
          <div>
            <span className="font-serif text-5xl text-sage-light/70">02</span>
            <h3 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">Terapia EMDR</h3>
            <p className="text-cream/70 leading-relaxed mt-5 max-w-md">
              Un approccio utilizzato da professionisti adeguatamente formati per lavorare su esperienze
              traumatiche o emotivamente difficili. Ogni intervento viene valutato individualmente.
            </p>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.2}>
        <div className="mt-14 flex flex-col sm:flex-row gap-4">
          <PillButton to="/approccio" variant="outline" className="border-cream/30 text-cream hover:bg-cream hover:text-ink" testid="home-approccio-link">
            Psicoterapia cognitivo-comportamentale <ArrowRight size={18} />
          </PillButton>
          <PillButton to="/approccio#emdr" variant="outline" className="border-cream/30 text-cream hover:bg-cream hover:text-ink" testid="home-emdr-link">
            Scopri l'approccio EMDR
          </PillButton>
        </div>
      </Reveal>
    </div>
    <div className="pointer-events-none absolute -bottom-32 -left-20 w-[380px] h-[380px] rounded-full bg-sage/20 blur-3xl" />
  </section>
);

const ClosingCta = () => (
  <section className="py-28 md:py-40" data-testid="home-closing">
    <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.28em] text-sage-dark mb-8">Un primo passo</p>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-ink tracking-tight leading-[1.02] text-balance">
          Quando senti che è il momento, questo è uno spazio per te.
        </h2>
      </Reveal>
      <Reveal delay={0.16}>
        <div className="mt-10 flex justify-center">
          <PillButton to="/contatti" testid="home-closing-cta">
            Richiedi un primo colloquio <ArrowRight size={18} />
          </PillButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const QuoteBand = () => (
  <section className="py-24 md:py-36 bg-sand" data-testid="home-quote">
    <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
      <Reveal>
        <span className="font-serif text-7xl md:text-8xl text-sage leading-none select-none">“</span>
      </Reveal>
      <Reveal delay={0.08}>
        <blockquote className="font-serif italic text-3xl md:text-5xl text-ink leading-[1.2] tracking-tight text-balance -mt-6">
          La speranza vede l'invisibile, tocca l'intangibile e raggiunge l'impossibile.
        </blockquote>
      </Reveal>
      <Reveal delay={0.16}>
        <p className="mt-8 text-xs uppercase tracking-[0.28em] text-sage-dark">— Anonimo</p>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  useSeo(
    "Psicologa Psicoterapeuta a Macerata | Susanna Paoletti",
    "Dott.ssa Susanna Paoletti, psicologa e psicoterapeuta a Macerata. Psicoterapia cognitivo-comportamentale ed EMDR. Scopri lo studio e i servizi.",
    "/"
  );
  return (
    <>
      <Hero />
      <EditorialMarquee />
      <Intro />
      <AreeTeaser />
      <Approcci />
      <QuoteBand />
      <ClosingCta />
    </>
  );
}
